import axios from 'axios';
import { SearchFilters, SearchTypes } from '../utils/search';
import { IN_PROGRESS_EVENTS_DATE } from '../pages/SearchPage/Filters/FilterComponents/DateFilter';
import {
    SearchTypeNames,
    SearchTypeGroups,
    SearchParamNames,
    SortDirections,
    Defaults,
    ApiEndpoints,
    DateFormats,
    EventSortFields,
    PublicPrivateValues,
} from './searchApiConstants';

// Cancel token management - using a dictionary to store tokens by search type
const cancelTokens = {};

/**
 * Cancels any previous search request
 */
const cancelPreviousSearch = (parallelSearchType) => {
    const key = parallelSearchType || Defaults.DEFAULT_SEARCH_KEY;
    const existingToken = cancelTokens[key];
    if (existingToken) {
        existingToken();
    }
};

/**
 * Sets the cancel token for the current search type
 */
const setCancelToken = (cancelToken, parallelSearchType) => {
    const key = parallelSearchType || Defaults.DEFAULT_SEARCH_KEY;
    cancelTokens[key] = cancelToken;
};

/**
 * Builds prize money and entry fee dictionaries from coreUI
 */
const buildPrizeMoneyAndEntryFeeDicts = (coreUI) => {
    const prizeMoneyDict = {};
    const entryFeeDict = {};
    let prizeMoneyMax = 0;
    let entryFeeMax = 0;

    if (coreUI?.types?.search?.prizeMoneyValues) {
        coreUI.types.search.prizeMoneyValues.forEach(pm => {
            prizeMoneyDict[pm.id] = pm.value;
        });
        prizeMoneyMax = coreUI.types.search.prizeMoneyValues.length;
    }

    if (coreUI?.types?.search?.entryFeeValue) {
        coreUI.types.search.entryFeeValue.forEach(ef => {
            entryFeeDict[ef.id] = ef.value;
        });
        entryFeeMax = coreUI.types.search.entryFeeValue.length;
    }

    return { prizeMoneyDict, entryFeeDict, prizeMoneyMax, entryFeeMax };
};

/**
 * Appends boolean filter entries to params
 */
const appendBooleanFilterEntries = (params, filterObject, paramName) => {
    if (filterObject) {
        Object.entries(filterObject).forEach(([key, bool]) => {
            if (key && bool) {
                params.append(paramName, key);
            }
        });
    }
};

/**
 * Builds player-specific filter parameters
 */
const buildPlayerFilters = (params, filters) => {
    if (filters.type !== SearchTypeNames.PLAYERS) {
      return;
    }

    if (filters.nationality) {
        params.append(SearchParamNames.NATIONALITY, filters.nationality);
    }
    if (filters.college) {
        params.append(SearchParamNames.COLLEGE_ID, filters.college);
    }
    if (filters.division) {
        params.append(SearchParamNames.DIVISION_ID, filters.division);
    }
    if (filters.conference) {
        params.append(SearchParamNames.CONFERENCE_ID, filters.conference);
    }
    if (filters.ageMin) {
        params.append(SearchParamNames.AGE_MIN, filters.ageMin);
    }
    if (filters.ageMax) {
        params.append(SearchParamNames.AGE_MAX, filters.ageMax);
    }
    if (filters.hsGradYears && Object.entries(filters.hsGradYears).length) {
        Object.entries(filters.hsGradYears).forEach(([year, bool]) => {
            if (year && bool) {
                params.append(SearchParamNames.GRAD_YEAR_HS, year);
            }
        });
    }
};

/**
 * Builds event-specific filter parameters
 */
const buildEventFilters = (params, filters, prizeMoneyDict, entryFeeDict, prizeMoneyMax, entryFeeMax) => {
    if (filters.type !== SearchTypeNames.EVENTS) {
      return;
    }

    appendBooleanFilterEntries(params, filters.tours, SearchParamNames.TOURS);
    appendBooleanFilterEntries(params, filters.eventTypes, SearchParamNames.EVENT_TYPES);
    appendBooleanFilterEntries(params, filters.eventUtrTypes, SearchParamNames.EVENT_UTR_TYPES);
    appendBooleanFilterEntries(params, filters.genders, SearchParamNames.GENDERS);
    appendBooleanFilterEntries(params, filters.ballTypes, SearchParamNames.BALL_TYPES);

    if (filters.hasPrizeMoney) {
        params.append(SearchParamNames.HAS_PRIZE_MONEY, filters.hasPrizeMoney);
    }
    if (filters.prizeMoneyMin && filters.prizeMoneyMin !== Defaults.DEFAULT_UTR_MIN) {
        params.append(SearchParamNames.EVENT_PRIZE_MONEY_MIN, prizeMoneyDict[filters.prizeMoneyMin]);
    }
    if (filters.prizeMoneyMax && filters.prizeMoneyMax !== prizeMoneyMax) {
        params.append(SearchParamNames.EVENT_PRIZE_MONEY_MAX, prizeMoneyDict[filters.prizeMoneyMax]);
    }
    if (filters.entryFeeMin && filters.entryFeeMin !== Defaults.DEFAULT_UTR_MIN) {
        params.append(SearchParamNames.EVENT_ENTRY_FEES_MIN, entryFeeDict[filters.entryFeeMin]);
    }
    if (filters.entryFeeMax && filters.entryFeeMax !== entryFeeMax) {
        params.append(SearchParamNames.EVENT_ENTRY_FEES_MAX, entryFeeDict[filters.entryFeeMax]);
    }
};

/**
 * Builds common filters for events, flex leagues, team leagues, etc.
 */
const buildCommonEventFilters = (params, filters) => {
    if (!SearchTypeGroups.EVENTS_WITH_COMMON_FILTERS.includes(filters.type)) {
      return;
    }

    const inProgressEventsFilter = filters.startDate?.isSame(IN_PROGRESS_EVENTS_DATE, 'day');

    if (filters.startDate) {
        params.append(SearchParamNames.RANGE, `eventSchedule.eventStartUtc>=${filters.startDate.format(DateFormats.API_DATE)}`);
    }
    if (filters.endDate) {
        params.append(SearchParamNames.RANGE, `eventSchedule.eventEndUtc<=${filters.endDate.format(DateFormats.API_DATE)}`);
        if (inProgressEventsFilter) {
            params.append(SearchParamNames.SORT, EventSortFields.START_UTC_DESC);
        } else {
            params.append(SearchParamNames.SORT, EventSortFields.END_UTC_DESC);
        }
    }
    if (inProgressEventsFilter) {
        params.append(SearchParamNames.SHOW_IN_PROGRESS_EVENTS, true);
    }
};

/**
 * Builds college-specific filter parameters
 */
const buildCollegeFilters = (params, filters) => {
    if (filters.type !== SearchTypeNames.COLLEGES) {
      return;
    }

    if (filters.publicPrivate) {
        params.append(SearchParamNames.PUBLIC, filters.publicPrivate === PublicPrivateValues.PUBLIC);
    }
    if (filters.division) {
        params.append(SearchParamNames.DIVISION_ID, filters.division);
    }
    if (filters.conference) {
        params.append(SearchParamNames.CONFERENCE_ID, filters.conference);
    }
    if (filters.utrFitRating) {
        params.append(SearchParamNames.UTR_FIT_RATING, filters.utrFitRating);
        params.append(SearchParamNames.UTR_FIT_POSITION, filters.utrFitPosition || Defaults.DEFAULT_UTR_FIT_POSITION);
    }
};

/**
 * Builds junior circuits-specific filter parameters
 */
const buildJuniorCircuitsFilters = (params, filters) => {
    if (filters.type !== SearchTypeNames.JUNIOR_CIRCUITS) {
      return;
    }

    if (filters.regionSearch) {
        params.append(SearchParamNames.REGION_SEARCH, filters.regionSearch);
    }
    appendBooleanFilterEntries(params, filters.eventRegions, SearchParamNames.EVENT_REGIONS);
    appendBooleanFilterEntries(params, filters.eventTypes, SearchParamNames.EVENT_TYPES);
    appendBooleanFilterEntries(params, filters.eventUtrTypes, SearchParamNames.EVENT_UTR_TYPES);
    appendBooleanFilterEntries(params, filters.genders, SearchParamNames.GENDERS);
    appendBooleanFilterEntries(params, filters.eventTeamTypes, SearchFilters.EVENT_TEAM_TYPES);

    if (filters.ageMin) {
        params.append(SearchParamNames.AGE_MIN, filters.ageMin);
    }
    if (filters.ageMax) {
        params.append(SearchParamNames.AGE_MAX, filters.ageMax);
    }
};

/**
 * Builds location aggregation filter parameters
 */
const buildLocationFilters = (params, filters) => {
    if (!SearchTypeGroups.LOCATION_FILTER_TYPES.includes(filters.type)) {
      return;
    }

    if (filters.locationCountryName) {
        params.append(SearchParamNames.LOCATION_COUNTRY_NAME, filters.locationCountryName);
    }
    if (filters.locationStateName) {
        params.append(SearchParamNames.LOCATION_STATE_NAME, filters.locationStateName);
    }
};

/**
 * Builds all search parameters from searchParams and filters
 */
const buildSearchParams = (searchParams, coreUI) => {
    const params = new URLSearchParams();
    const { query, filters, top, skip } = searchParams;

    // Basic query and pagination
    if (query) {
        params.append(SearchParamNames.QUERY, query);
    }
    params.append(SearchParamNames.TOP, top || Defaults.DEFAULT_TOP);
    params.append(SearchParamNames.SKIP, skip || Defaults.DEFAULT_SKIP);

    if (!filters) {
        return params;
    }

    // Build prize money and entry fee dictionaries
    const { prizeMoneyDict, entryFeeDict, prizeMoneyMax, entryFeeMax } = buildPrizeMoneyAndEntryFeeDicts(coreUI);

    // Common filters
    if (filters.distance && filters.lat && filters.lng) {
        params.append(SearchParamNames.DISTANCE, filters.distance);
        params.append(SearchParamNames.PIN, `${filters.lat},${filters.lng}`);
    }
    if (filters.gender) {
        params.append(SearchParamNames.GENDER, filters.gender);
    }
    if (filters.primaryTag) {
        params.append(SearchParamNames.PRIMARY_TAGS, filters.primaryTag);
    }
    if (filters.ageTag) {
        params.append(SearchParamNames.AGE_TAGS, filters.ageTag);
    }
    if (filters.utrMin > Defaults.DEFAULT_UTR_MIN) {
        params.append(SearchParamNames.UTR_MIN, filters.utrMin);
    }
    if (filters.utrMax < Defaults.DEFAULT_UTR_MAX) {
        params.append(SearchParamNames.UTR_MAX, filters.utrMax);
    }
    if (filters.utrType) {
        params.append(SearchParamNames.UTR_TYPE, filters.utrType);
    }
    if (filters.utrTeamType) {
        params.append(SearchParamNames.UTR_TEAM_TYPE, filters.utrTeamType);
    }
    if (filters.utrReliability) {
        params.append(SearchParamNames.UTR_RELIABILITY, filters.utrReliability);
    }

    // Type-specific filters
    buildPlayerFilters(params, filters);
    buildEventFilters(params, filters, prizeMoneyDict, entryFeeDict, prizeMoneyMax, entryFeeMax);
    buildCommonEventFilters(params, filters);
    buildCollegeFilters(params, filters);
    buildJuniorCircuitsFilters(params, filters);
    buildLocationFilters(params, filters);

    // Sport type filters (for flex leagues, events, players, clubs)
    if (
        filters.type === SearchTypes.FLEX_LEAGUES ||
        filters.type === SearchTypes.EVENTS ||
        filters.type === SearchTypes.PLAYERS ||
        filters.type === SearchTypes.CLUBS
    ) {
        appendBooleanFilterEntries(params, filters.eventTeamTypes, SearchFilters.EVENT_TEAM_TYPES);

        const sportTypes = filters?.sportTypes || {};
        params.append(SearchFilters.SHOW_TENNIS_CONTENT, !!sportTypes.tennis);
        params.append(SearchFilters.SHOW_PICKLEBALL_CONTENT, !!sportTypes.pickleball);
    }

    // School/club search flag
    if (!filters.type || SearchTypeGroups.SCHOOL_CLUB_SEARCH_TYPES.includes(filters.type)) {
        params.append(SearchParamNames.SCHOOL_CLUB_SEARCH, Defaults.SCHOOL_CLUB_SEARCH_VALUE);
    }

    return params;
};

/**
 * Main search function
 * @param {Object} axiosInstance - Axios instance
 * @param {Object} coreUI - Core UI configuration
 * @param {Object} searchParams - Search parameters (query, filters, top, skip, appendType, sort)
 * @param {string} searchOrigin - Origin of the search
 * @param {string} parallelSearchType - Type of parallel search (for cancel token management)
 * @returns {Promise} Axios promise
 */
export const search = (axiosInstance, coreUI, searchParams, searchOrigin, parallelSearchType) => {
    console.log('search', searchParams);
    const { filters, skip, appendType, sort } = searchParams;

    // Cancel any previous search
    cancelPreviousSearch(parallelSearchType);

    // Build search parameters
    const params = buildSearchParams(searchParams, coreUI);

    // Add sort parameter
    if (sort) {
        params.append(SearchParamNames.SORT, `${sort.field}:${sort.asc ? SortDirections.ASC : SortDirections.DESC}`);
    }

    // Add search origin
    if (searchOrigin) {
        params.append(SearchParamNames.SEARCH_ORIGIN, searchOrigin);
    }

    // Build URL
    const urlPath = (skip || appendType) && filters?.type ? `/${filters.type}` : '';
    const url = `${ApiEndpoints.SEARCH_BASE}${urlPath}`;

    // Make request with cancel token
    const { CancelToken } = axios;
    return axiosInstance.get(url, {
        params,
        cancelToken: new CancelToken((c) => {
            setCancelToken(c, parallelSearchType);
        }),
    });
};
